import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import {
  doGetDocumentPdfAnnotations,
  doPutDocumentPdfAnnotations,
  luHandleAppiError,
  type U3140DocumentLogicalUnit,
  type U3140DocumentPdfAnnotationsGetResult,
  type U3140DocumentPdfAnnotationsPayload,
} from "../../U3140PdfAnnotationsService";

export interface U3140UsePdfAnnotationHandlersParams {
  documentLogicalUnit: U3140DocumentLogicalUnit;
  /** Namespace i18n pour les toasts d’erreur (ex. `u3140`). */
  i18nNamespace: string;
  doGetDocumentPdfAnnotationsFn?: (
    documentId: string | number
  ) => Promise<U3140DocumentPdfAnnotationsGetResult>;
  doPutDocumentPdfAnnotationsFn?: (
    documentId: string | number,
    payload: U3140DocumentPdfAnnotationsPayload
  ) => Promise<unknown>;
  onError?: (error: unknown) => void;
}

export function useU3140UsePdfAnnotationHandlers(params: U3140UsePdfAnnotationHandlersParams) {
  const axios = useU2000AxiosAuthenticated();
  const { i18nNamespace } = params;
  const { t } = useTranslation(i18nNamespace);
  const toast = useToast();
  const { documentLogicalUnit } = params;
  const doGetOverride = params.doGetDocumentPdfAnnotationsFn;
  const doPutOverride = params.doPutDocumentPdfAnnotationsFn;
  const onError = params.onError;

  const loadAnnotations = useCallback(
    async (documentId: string | number) => {
      try {
        const fn =
          doGetOverride ??
          ((id: string | number) => doGetDocumentPdfAnnotations(axios, id, documentLogicalUnit));
        return await fn(documentId);
      } catch (error: unknown) {
        onError?.(error);
        const err = U2000HandleApiError(
          error as Parameters<typeof U2000HandleApiError>[0],
          t,
          i18nNamespace,
          luHandleAppiError
        );
        if (err?.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        throw error;
      }
    },
    [axios, documentLogicalUnit, doGetOverride, i18nNamespace, onError, t, toast]
  );

  const saveAnnotations = useCallback(
    async (documentId: string | number, payload: U3140DocumentPdfAnnotationsPayload) => {
      try {
        const fn =
          doPutOverride ??
          ((id: string | number, p: U3140DocumentPdfAnnotationsPayload) =>
            doPutDocumentPdfAnnotations(axios, id, p, documentLogicalUnit));
        await fn(documentId, payload);
      } catch (error: unknown) {
        onError?.(error);
        const err = U2000HandleApiError(
          error as Parameters<typeof U2000HandleApiError>[0],
          t,
          i18nNamespace,
          luHandleAppiError
        );
        if (err?.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
        throw error;
      }
    },
    [axios, documentLogicalUnit, doPutOverride, i18nNamespace, onError, t, toast]
  );

  return { loadAnnotations, saveAnnotations };
}
